import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { interval } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { TenantService } from '../../../core/tenant/tenant.service';
import type { NotificationItem } from '../models/notifications.models';
import { NotificationsApiService } from '../services/notifications-api.service';

const DEFAULT_PAGE_SIZE = 20;
/** Atualiza o badge do sino sem precisar abrir o painel (além de focus/visibility). */
const UNREAD_POLL_MS = 20_000;

@Injectable({ providedIn: 'root' })
export class NotificationsStore {
  private readonly tenant = inject(TenantService);
  private readonly api = inject(NotificationsApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly itemsSignal = signal<NotificationItem[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly loadingMoreSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly unreadCountSignal = signal(0);
  private readonly pageSignal = signal(1);
  private readonly pageSizeSignal = signal(DEFAULT_PAGE_SIZE);
  private readonly totalCountSignal = signal(0);
  private readonly hasMoreSignal = signal(false);

  readonly items = this.itemsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loadingMore = this.loadingMoreSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly unreadCount = this.unreadCountSignal.asReadonly();
  readonly page = this.pageSignal.asReadonly();
  readonly pageSize = this.pageSizeSignal.asReadonly();
  readonly hasMore = this.hasMoreSignal.asReadonly();

  readonly hasUnread = computed(() => this.unreadCount() > 0);
  readonly isEmpty = computed(() => !this.loading() && !this.error() && this.items().length === 0);

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.reset();
        return;
      }

      if (this.loadedCompanyIdSignal() !== companyId) {
        this.loadedCompanyIdSignal.set(companyId);
        this.reset();
        void this.loadUnreadCount();
      }
    });

    interval(UNREAD_POLL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.tenant.companyId()) {
          void this.loadUnreadCount();
        }
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        debounceTime(350),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.tenant.companyId()) return;
        void this.loadUnreadCount();
      });
  }

  async loadNotifications(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    this.pageSignal.set(1);
    try {
      const result = await this.api.getNotifications({
        page: 1,
        pageSize: this.pageSize(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.set(result.items);
      this.totalCountSignal.set(result.totalCount);
      this.hasMoreSignal.set(result.hasMore);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.set([]);
      this.totalCountSignal.set(0);
      this.hasMoreSignal.set(false);
      this.errorSignal.set('notifications.error');
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingSignal.set(false);
    }
  }

  async loadUnreadCount(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;
    try {
      const result = await this.api.getUnreadCount();
      if (!this.isSameCompany(requestCompanyId)) return;
      this.unreadCountSignal.set(result.unreadCount);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.unreadCountSignal.set(0);
    }
  }

  async markAsRead(id: string): Promise<void> {
    const notification = this.items().find((item) => item.id === id);
    if (!notification || notification.isRead) return;
    try {
      await this.api.markAsRead(id);
      this.itemsSignal.update((items) =>
        items.map((item) => (item.id === id ? { ...item, isRead: true, readAt: new Date().toISOString() } : item))
      );
      this.unreadCountSignal.update((count) => Math.max(count - 1, 0));
    } catch {
      await this.loadUnreadCount();
    }
  }

  async markAllAsRead(): Promise<void> {
    this.errorSignal.set(null);
    try {
      await this.api.markAllAsRead();
      this.itemsSignal.update((items) => items.map((item) => ({ ...item, isRead: true })));
      this.unreadCountSignal.set(0);
      await this.loadUnreadCount();
    } catch {
      this.errorSignal.set('notifications.error');
    }
  }

  async loadMore(): Promise<void> {
    if (!this.hasMore() || this.loadingMore()) return;
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingMoreSignal.set(true);
    const nextPage = this.page() + 1;
    try {
      const result = await this.api.getNotifications({
        page: nextPage,
        pageSize: this.pageSize(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;

      this.itemsSignal.update((items) => {
        const seen = new Set(items.map((i) => i.id));
        const merged = [...items];
        for (const item of result.items) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push(item);
          }
        }
        return merged;
      });
      this.pageSignal.set(nextPage);
      this.totalCountSignal.set(result.totalCount);
      this.hasMoreSignal.set(result.hasMore);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.errorSignal.set('notifications.error');
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingMoreSignal.set(false);
    }
  }

  reset(): void {
    this.itemsSignal.set([]);
    this.loadingSignal.set(false);
    this.loadingMoreSignal.set(false);
    this.errorSignal.set(null);
    this.unreadCountSignal.set(0);
    this.pageSignal.set(1);
    this.pageSizeSignal.set(DEFAULT_PAGE_SIZE);
    this.totalCountSignal.set(0);
    this.hasMoreSignal.set(false);
  }

  private isSameCompany(requestCompanyId: string): boolean {
    return this.tenant.companyId() === requestCompanyId;
  }
}
